import React, { useRef, useState, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { Space } from "antd"

import { useAppContext } from "@components/AppContext"
import { MODAL_LARGE_WIDTH } from "@components"
import { Modal, Stack, ImportModalErrors } from "@components/Page"

import Import from "./Import"
import listProjectFiles from "./helpers/listProjectFiles"
import TargetProjectSelect from "./TargetProjectSelect"
import { InvestmentShape } from "../shapes"
import listProjectTransactions from "./helpers/listProjectTransactions"
import InvestmentsTransferTable from "./InvestmentsTransferTable"
import InvestmentsTransferFooter from "./InvestmentsTransferFooter"

const LABEL_TITLE = "Transfer Investments"


const InvestmentsTransferModal = ({
  modal,
  projectId,
  onSuccess,
  investments,
}) => {
  const table = useRef({})

  const [ investmentIds, setInvestmentIds ] = useState([])

  const [ sourceFiles, setSourceFiles ] = useState([])
  const [ sourceBatches, setSourceBatches ] = useState([])
  const [ sourceTransactions, setSourceTransactions ] = useState([])

  const [ targetBatches, setTargetBatches ] = useState([])
  const [ targetProjectId, setTargetProjectId ] = useState()
  const [ targetInvestments, setTargetInvestments ] = useState([])

  const [ customAmounts, setCustomAmounts ] = useState([])
  const [ customTargets, setCustomTargets ] = useState([])

  const [ errors, setErrors ] = useState([])

  const { request } = useAppContext()

  const sourceInvestments = useMemo(() => {
    const hasCustomAmounts = customAmounts.length > 0

    if (!hasCustomAmounts) {
      return investments.filter(({ id }) => investmentIds.includes(id))
    }

    const shouldInclude = id =>
      !!customAmounts.find(({ sourceInvestmentId }) => id === sourceInvestmentId)

    return investments
      .filter(({ id }) => investmentIds.includes(id))
      .filter(({ id }) => shouldInclude(id))
  }, [
    investments,
    investmentIds,
    customAmounts,
  ])

  const getSourceBatchName = useCallback((id) =>
    sourceBatches.find(batch => batch.id === id)?.name
  , [ sourceBatches ])

  modal.current.open = async (sourceInvestmentIds, afterAction) => {
    setInvestmentIds(sourceInvestmentIds)

    const [ batches, files ] = await listProjectFiles(request, projectId)
    setSourceFiles(files)
    setSourceBatches(batches)

    const transactions = await listProjectTransactions(request, projectId)
    setSourceTransactions(transactions)
  }

  const onClose = () => {
    setInvestmentIds([])

    setSourceFiles([])
    setSourceBatches([])
    setSourceTransactions([])

    setTargetBatches([])
    setTargetProjectId()
    setTargetInvestments([])

    setCustomAmounts([])
    setCustomTargets([])

    setErrors([])
  }

  const isOpen = investmentIds.length > 0;

  const modalProps = {
    title: LABEL_TITLE,
    width: MODAL_LARGE_WIDTH,
    isOpen,
    onClose,
  }

  const transferTableProps = {
    table,
    sourceProjectId: projectId,
    sourceFiles,
    sourceInvestments,
    sourceTransactions,
    getSourceBatchName,
    targetProjectId,
    targetInvestments,
    customAmounts,
    customTargets,
    setCustomTargets,
  }

  const hasErrors = errors.length > 0

  const onAfterComplete = async () => {
    await onSuccess()
    onClose()
  }

  return (
    <Modal {...modalProps}>
      <Stack>
        <Space>
          <TargetProjectSelect
            sourceProjectId={projectId}
            targetProjectId={targetProjectId}
            setTargetBatches={setTargetBatches}
            setTargetProjectId={setTargetProjectId}
            setTargetInvestments={setTargetInvestments}
          />

          {
            targetProjectId && (
              <Import
                table={table}
                targetProjectId={targetProjectId}
                sourceProjectId={projectId}
                sourceInvestments={sourceInvestments}
                setCustomAmounts={setCustomAmounts}
                setErrors={setErrors}
              />
            )
          }
        </Space>

        {
          hasErrors && (
            <ImportModalErrors
              title="CSV Template Errors"
              prefix="Row "
              errors={errors}
            />
          )
        }

        <InvestmentsTransferTable
          {...transferTableProps}
        />

        <InvestmentsTransferFooter
          key={targetProjectId}
          table={table}
          sourceProjectId={projectId}
          sourceFiles={sourceFiles}
          sourceBatches={sourceBatches}
          sourceInvestments={sourceInvestments}
          sourceTransactions={sourceTransactions}
          targetBatches={targetBatches}
          targetProjectId={targetProjectId}
          targetInvestments={targetInvestments}
          customAmounts={customAmounts}
          customTargets={customTargets}
          setTargetBatches={setTargetBatches}
          onAfterComplete={onAfterComplete}
        />
      </Stack>
    </Modal>
  )
}

InvestmentsTransferModal.propTypes = {
  modal: PropTypes.shape().isRequired,
  projectId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func.isRequired,
  investments: PropTypes.arrayOf(InvestmentShape).isRequired,
}

export default InvestmentsTransferModal;
